import dayjs from "dayjs";

export interface IRollModel {
  rollId: number;
  active: boolean;
  availabilityId: number;
  sessionTypeId: number;
  rollClassificationId?: number | undefined;
  rollName?: string | undefined;
  govCareTypeId: number;
  govCareTypeCode?: string | undefined;
  govCareTypeDescription?: string | undefined;
  sessionTypeDeleted: boolean;
  labourCareTypeId: number;
  labourCareTypeDescription?: string | undefined;
  serviceId: number;
  shortName?: string | undefined;
  serviceStatusId: number;
  earliestOpenTime?: dayjs.Dayjs | undefined;
  latestOpenTime?: dayjs.Dayjs | undefined;
  latestClosingTime?: dayjs.Dayjs | undefined;
  earliestClosingTime?: dayjs.Dayjs | undefined;
  monIsOpen?: boolean | undefined;
  tueIsOpen?: boolean | undefined;
  wedIsOpen?: boolean | undefined;
  thuIsOpen?: boolean | undefined;
  friIsOpen?: boolean | undefined;
  satIsOpen?: boolean | undefined;
  sunIsOpen?: boolean | undefined;
  monStart?: dayjs.Dayjs | undefined;
  tueStart?: dayjs.Dayjs | undefined;
  wedStart?: dayjs.Dayjs | undefined;
  thuStart?: dayjs.Dayjs | undefined;
  friStart?: dayjs.Dayjs | undefined;
  satStart?: dayjs.Dayjs | undefined;
  sunStart?: dayjs.Dayjs | undefined;
  monEnd?: dayjs.Dayjs | undefined;
  tueEnd?: dayjs.Dayjs | undefined;
  wedEnd?: dayjs.Dayjs | undefined;
  thuEnd?: dayjs.Dayjs | undefined;
  friEnd?: dayjs.Dayjs | undefined;
  satEnd?: dayjs.Dayjs | undefined;
  sunEnd?: dayjs.Dayjs | undefined;
  licensedPlaces?: number | undefined;
  licensedPlacesWithoutOperationalCapacityCap?: number | undefined;
  operationalCapacity?: number | undefined;
  defaultCasualFeeId: number;
  defaultCasualFeeName?: string | undefined;
  defaultCasualFeeAmount: number;
  defaultPermFeeId: number;
  defaultPermFeeName?: string | undefined;
  defaultPermFeeAmount?: number | undefined;
  effectiveFrom?: dayjs.Dayjs | undefined;
  effectiveTo?: dayjs.Dayjs | undefined;
  usesOshcFeeStructure?: boolean | undefined;
  minAgeMths: number;
  maxAgeMths: number;
  currentLDCChildrenPerEducator?: number | undefined;
  allowELCBookings: boolean;
  daysOpen?: number | undefined;
  ageCalculationMethodId: number;
  stateDefaultMinAge: number;
  stateDefaultMaxAge: number;
  stateDefaultELCMinAge: number;
  defaultMinAgeMths: number;
  defaultMaxAgeMths: number;
}

export interface IServiceInformationFlyer {
  serviceId: number;
  serviceName: string;
  isFboService: boolean;
  hasUnconfirmedTermDates: boolean;
  allowQuery: boolean;
  hasAcceptedFees: boolean;
  acceptOrAcknowledgeFee: string;
  currentFees: IServiceFees | null;
  proposedFees: IServiceFees | null;
}

export interface IServiceFees {
  bscFeeId: number;
  bscFeeAmount?: number | null;
  bscLowFeeAmount?: number | null;
  bscHighFeeAmount?: number | null;
  bscStartTime?: dayjs.Dayjs | null;
  bscEndTime?: dayjs.Dayjs | null;
  bscElcFeeId: number;
  bscElcFeeAmount?: number | null;
  bscElcStartTime?: dayjs.Dayjs | null;
  bscElcEndTime?: dayjs.Dayjs | null;
  bscIncreaseSessionFeeAfterMaxSubsidy?: number | null;
  bscelcIncreaseSessionFeeAfterMaxSubsidy?: number | null;

  ascFeeId: number;
  ascFeeAmount?: number | null;
  ascLowFeeAmount?: number | null;
  ascHighFeeAmount?: number | null;
  ascStartTime?: dayjs.Dayjs | null;
  ascEndTime?: dayjs.Dayjs | null;
  ascElcFeeId: number;
  ascElcFeeAmount?: number | null;
  ascElcStartTime?: dayjs.Dayjs | null;
  ascElcEndTime?: dayjs.Dayjs | null;
  ascIncreaseSessionFeeAfterMaxSubsidy?: number | null;
  ascelcIncreaseSessionFeeAfterMaxSubsidy?: number | null;

  vacFeeId: number;
  vacFeeAmount?: number | null;
  vacStartTime?: dayjs.Dayjs | null;
  vacEndTime?: dayjs.Dayjs | null;

  pfdFeeId: number;
  pfdFeeAmount?: number | null;

  allowFeeQuery: boolean;
}

export interface IFeeQuery {
  feeQueryId?: number;
  serviceId: number;
  schoolContactId?: number;
  isAccepted: boolean;
  isFboAccepted: boolean;
  comment?: string;
  dateCreated?: dayjs.Dayjs;
  actionedButton?: string;
  autoLoginKey?: string;

  currentBscFeeId?: number;
  currentBscAmount?: number;
  currentAscFeeId?: number;
  currentAscAmount?: number;
  currentVacFeeId?: number;
  currentVacAmount?: number;
  currentPfdFeeId?: number;
  currentPfdAmount?: number;

  proposedBscFeeId?: number;
  proposedBscAmount?: number;
  proposedAscFeeId?: number;
  proposedAscAmount?: number;
  proposedVacFeeId?: number;
  proposedVacAmount?: number;
  proposedPfdFeeId?: number;
  proposedPfdAmount?: number;
}

export interface ICcsInformation {
  maxCcsRate: number;
  ccsHourlyRateCap: number;
}

export interface IFeeInformationFlyerViewModel {
  title: string;
  showOverlay: boolean;
  serviceInformationFlyer: IServiceInformationFlyer;
  activeRolls: IRollModel[];
  feeQueries: IFeeQuery[];
  lastFeeQuery?: IFeeQuery | null;
  sendComms: boolean;
  principalResponseDueDate?: dayjs.Dayjs | null;
  parentCommunicationDate?: dayjs.Dayjs | null;
  showTermInEffectiveFrom: boolean;
  effectiveFromDate?: dayjs.Dayjs | null;
  effectiveFromTerm?: string | null;
  useLastYearCcsForCurrent: boolean;
  lastYearCcsInfo?: ICcsInformation | null;
  currentYearCcsInfo?: ICcsInformation | null;
  buttonActionType?: string;
}

/** Fee query request model (without serviceId - serviceId is passed as route parameter) */
export interface IFeeQueryRequest {
  schoolContactId?: number;
  isAccepted: boolean;
  isFboAccepted: boolean;
  comment?: string;

  currentBscFeeId?: number;
  currentBscAmount?: number;
  currentAscFeeId?: number;
  currentAscAmount?: number;
  currentVacFeeId?: number;
  currentVacAmount?: number;

  proposedBscFeeId?: number;
  proposedBscAmount?: number;
  proposedAscFeeId?: number;
  proposedAscAmount?: number;
  proposedVacFeeId?: number;
  proposedVacAmount?: number;
}

export interface IFeeQuerySaveRequest {
  query: IFeeQueryRequest;
  feeAction: string;
  sendComms: boolean;
}

export interface IFeeQuerySaveResponse {
  isSuccess: boolean;
  error?: string;
  resultObject?: {
    hasUnconfirmedTermDates: boolean;
  };
}

export enum FeeActionEnum {
  Accept = "Accept",
  Acknowledge = "Acknowledge",
  Reject = "Reject",
  OptOut = "Opt-out",
  Contact = "Contact",
}
